import { isMatanahAmountPending, pledgePayAmountDue } from './pledge-pay-utils';

let payPledgeMap = null;
let payPledgeKeys = [];

function pledgeKey(tableName, pledgeId) {
    return `${tableName}-${pledgeId}`;
}

function initPayPledgeMap(map) {
    if (Array.isArray(map) && map.length === 0) {
        map = {};
    }
    if (!map || typeof map !== 'object' || Array.isArray(map)) {
        throw new Error('initPayPledgeMap: map object is required');
    }
    payPledgeMap = map;
}

function getPayPledgeMap() {
    if (payPledgeMap === null) {
        throw new Error('getPayPledgeMap: pay pledge map is not initialized; call initPayPledgeMap first');
    }
    return payPledgeMap;
}

function getPayPledge(key) {
    return getPayPledgeMap()[key] ?? null;
}

function resolvePledgeRow(tableName, pledgeId) {
    return getPayPledge(pledgeKey(tableName, pledgeId));
}

function getPayPledges(keys) {
    if (!keys) {
        return [];
    }
    const list = Array.isArray(keys) ? keys : [keys];
    const seen = new Set();
    const pledges = [];
    list.forEach((key) => {
        const k = String(key);
        if (!k || seen.has(k)) {
            return;
        }
        seen.add(k);
        const pledge = getPayPledge(k);
        if (pledge) {
            pledges.push(pledge);
        }
    });
    return pledges;
}

function dedupePledgeKeys(keys) {
    if (!keys) {
        return [];
    }
    const list = Array.isArray(keys) ? keys : [keys];
    const seen = new Set();
    const out = [];
    list.forEach((key) => {
        const k = String(key);
        if (k && !seen.has(k)) {
            seen.add(k);
            out.push(k);
        }
    });
    return out;
}

function dedupePledgesByKey(pledges) {
    const seen = new Set();
    return (pledges || []).filter((p) => {
        const key = pledgeKey(p.table_name, p.id);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

function requirePayPledges(keys) {
    const list = dedupePledgeKeys(keys);
    const pledges = dedupePledgesByKey(getPayPledges(list));
    if (!pledges.length) {
        throw new Error('requirePayPledges: no pledges found for keys');
    }
    if (pledges.length !== list.length) {
        throw new Error('requirePayPledges: one or more selected pledges are missing from the map');
    }
    return pledges;
}

function mergePayPledgeRow(tableName, pledgeId, fields) {
    const key = pledgeKey(tableName, pledgeId);
    const map = { ...getPayPledgeMap() };
    const current = map[key];
    if (!current) {
        throw new Error(`mergePayPledgeRow: unknown pledge key ${key}`);
    }
    map[key] = { ...current, ...fields };
    initPayPledgeMap(map);
}

function mergePayPledgeMap(partial, removeKeys) {
    const map = payPledgeMap && typeof payPledgeMap === 'object' && !Array.isArray(payPledgeMap)
        ? { ...payPledgeMap }
        : {};
    if (partial && typeof partial === 'object' && !Array.isArray(partial)) {
        Object.keys(partial).forEach((key) => {
            map[key] = partial[key];
        });
    }
    (Array.isArray(removeKeys) ? removeKeys : []).forEach((key) => {
        delete map[String(key)];
    });
    initPayPledgeMap(map);
    payPledgeKeys = payPledgeKeys.filter((key) => Object.prototype.hasOwnProperty.call(map, key));
}

function setSelectedPayPledges(pledges) {
    if (!Array.isArray(pledges)) {
        throw new Error('setSelectedPayPledges: pledges array is required');
    }
    payPledgeKeys = pledges.map((p) => pledgeKey(p.table_name, p.id));
    const map = { ...getPayPledgeMap() };
    pledges.forEach((p) => {
        const key = pledgeKey(p.table_name, p.id);
        map[key] = { ...map[key], ...p };
    });
    initPayPledgeMap(map);
}

function getSelectedPayPledges() {
    return getPayPledges(payPledgeKeys);
}

function updateSelectedPayPledge(tableName, pledgeId, fields) {
    mergePayPledgeRow(tableName, pledgeId, fields);
}

function getPayPledgeFromTableRow($row) {
    const pledgeId = $row.data('pledge-id');
    if (pledgeId === undefined || pledgeId === null || pledgeId === '') {
        throw new Error('getPayPledgeFromTableRow: row missing data-pledge-id');
    }
    const tableName = $row.data('table-name') || 'pledge';
    const pledge = getPayPledge(pledgeKey(tableName, pledgeId));
    if (!pledge) {
        throw new Error(`getPayPledgeFromTableRow: pledge not in map (${tableName}-${pledgeId})`);
    }
    return pledge;
}

function getPledgeRowAmountToPay($row) {
    const $inp = $row.find('input[name="amount_paid"]');
    if ($inp.length && !$inp.prop('disabled')) {
        const raw = String($inp.val() ?? '').replace(/,/g, '').trim();
        const num = parseFloat(raw);
        if (raw !== '' && Number.isFinite(num)) {
            return num;
        }
    }
    const fromAttr = parseFloat(String($row.attr('data-amount-to-pay') ?? '').replace(/,/g, ''));
    if (Number.isFinite(fromAttr)) {
        return fromAttr;
    }
    const pledge = getPayPledgeFromTableRow($row);
    const due = pledgePayAmountDue(pledge);
    return isMatanahAmountPending(pledge, due) ? Number(pledge.amount) || 0 : due;
}

function getPledgeRowBalanceDue($row) {
    const fromAttr = parseFloat(String($row.attr('data-balance-due') ?? '').replace(/,/g, ''));
    if (Number.isFinite(fromAttr)) {
        return fromAttr;
    }
    return pledgePayAmountDue(getPayPledgeFromTableRow($row));
}

export {
    pledgeKey,
    initPayPledgeMap,
    getPayPledgeMap,
    getPayPledge,
    resolvePledgeRow,
    getPayPledges,
    requirePayPledges,
    setSelectedPayPledges,
    getSelectedPayPledges,
    updateSelectedPayPledge,
    mergePayPledgeRow,
    mergePayPledgeMap,
    getPayPledgeFromTableRow,
    getPledgeRowAmountToPay,
    getPledgeRowBalanceDue,
    dedupePledgeKeys,
    dedupePledgesByKey,
};
